import { useCallback, useEffect, useRef } from 'react';

import { enterFullscreen, exitFullscreen, fullscreenElement, useFullscreenExit } from './media.js';

/**
 * Full screen for a screen's overlays (a camera viewer, a whole wall).
 *
 * `enter()` must be called from the tap that opens the overlay, since
 * browsers only grant full screen inside a user gesture. The session
 * remembers whether it was the one that asked, so closing a viewer
 * never takes a kiosk that was already full screen out of it. When the
 * browser leaves full screen by itself (Escape, a system gesture) while
 * `active`, `onExit` closes the overlays with it.
 */
export function useFullscreenSession(active, onExit) {
    const session = useRef({ owns: false });

    useFullscreenExit(active, () => {
        session.current.owns = false;
        onExit();
    });

    useEffect(() => {
        const state = session.current;
        return () => {
            if (state.owns) {
                state.owns = false;
                exitFullscreen();
            }
        };
    }, []);

    const enter = useCallback(() => {
        if (fullscreenElement()) return;
        enterFullscreen();
        if (typeof document !== 'undefined' && (document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen)) {
            session.current.owns = true;
        }
    }, []);

    const leave = useCallback(() => {
        if (!session.current.owns) return;
        session.current.owns = false;
        exitFullscreen();
    }, []);

    return { enter, leave };
}
